import { useState, useEffect } from 'react';

// components
import Image from 'next/image';
import { Icon } from '@/components/ui/icon';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const HowItWorksCarousel = ({ items }) => {
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollSnaps = () => {
      setScrollSnaps(api.scrollSnapList());
    };

    // Initialize state from external carousel API
    // setTimeout defers state updates to avoid cascading renders
    setTimeout(() => {
      updateScrollSnaps();
      setCurrent(api.selectedScrollSnap() + 1);
    }, 0);

    // Subscribe to carousel events (external system updates)
    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', handleSelect);

    const handleResize = () => {
      setTimeout(updateScrollSnaps, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      api.off('select', handleSelect);
      window.removeEventListener('resize', handleResize);
    };
  }, [api]);

  const onSelectItem = (e) => {
    const index = Number(e.currentTarget.dataset.id) || 0;

    if (api) {
      api.scrollTo(index);
    }
  };

  return (
    <div className="mx-auto my-10 w-[100vw] xl:w-(--container-width)">
      <VisibleLoading delay={300}>
        {({ ref, className }) => (
          <div ref={ref} className={`my-8 w-full xl:my-16 ${className || ''}`.trim()}>
            <Carousel
              setApi={setApi}
              opts={{
                align: 'center',
                breakpoints: {
                  '(min-width: 1280px)': { active: false }
                }
              }}
            >
              <CarouselContent>
                {(items || []).map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="xl:max-w-auto xl:max-w-auto flex min-h-[340px] max-w-[280px] flex-col gap-6 xl:basis-1/5 xl:gap-12"
                  >
                    <div className="flex w-full items-center justify-between">
                      <h6 className="flex-1 text-center">{item.title}</h6>

                      {index < items.length - 1 && (
                        <div className="text-brand-400 w-6">
                          <Icon type="chevronRight" width={24} height={24} />
                        </div>
                      )}
                    </div>

                    <div className="flex w-full flex-1 justify-center">
                      <div style={{ maxWidth: item.width || '100%' }}>
                        <Image
                          src={getImagePath(item.image)}
                          width={item.image.width}
                          height={item.image.height}
                          alt={item.image.alternativeText || 'Feature'}
                          className={`h-full w-full rounded-3xl object-contain`}
                        />
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {scrollSnaps.length > 1 && (
              <div className="mt-5 flex justify-center gap-5 lg:mt-10 lg:gap-8">
                {scrollSnaps.map((_, index) => (
                  <div
                    key={index}
                    data-id={index}
                    className="group flex cursor-pointer items-center justify-center p-2"
                    onClick={onSelectItem}
                  >
                    <div
                      className={`h-[8px] w-[8px] cursor-pointer rounded-full lg:h-[10px] lg:w-[10px] ${current === index + 1 ? 'bg-brand-400' : 'bg-grey-500'} `}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </VisibleLoading>
    </div>
  );
};
export default HowItWorksCarousel;
